/**
 * SVG connection lines between parent and child nodes in the tree.
 */

import { useMemo } from 'react';
import type { LayoutNode } from '../types';

export interface TreeConnectionsProps {
  layoutNodes: LayoutNode[];
  expandedNodes: Set<string>;
  /** Skip drawing connection when parent is string and child is module (inline grid) */
  skipStringToModule?: boolean;
}

export default function TreeConnections({
  layoutNodes,
  expandedNodes,
  skipStringToModule = true
}: TreeConnectionsProps) {
  const layoutNodeById = useMemo(() => {
    const m = new Map<string, LayoutNode>();
    for (const n of layoutNodes) {
      m.set(n.equipment.id, n);
    }
    return m;
  }, [layoutNodes]);

  return (
    <svg className="absolute top-0 left-0 pointer-events-none" style={{ width: '100%', height: '100%', zIndex: 0 }}>
      {layoutNodes.map(node => {
        const { equipment, x, y, width, height } = node;
        return equipment.childEquipmentIds.map(childId => {
          const childNode = layoutNodeById.get(childId);
          if (!childNode || childNode.equipment.isGhost) return null;

          const isString = equipment.assetType.toLowerCase().includes('string');
          const childIsModule =
            childNode.equipment.assetType.toLowerCase().includes('module') ||
            childNode.equipment.assetType.toLowerCase().includes('solar');
          if (skipStringToModule && isString && childIsModule) return null;
          if (!expandedNodes.has(equipment.id)) return null;

          const parentX = x;
          const parentBottomY = y + height;
          const childTopY = childNode.y;
          const childX = childNode.x;
          const verticalGap = childTopY - parentBottomY;
          /** Run the horizontal segment in the corridor just under the child row (not mid-gap), so it does not cut through sibling cards */
          const clearance = Math.min(12, Math.max(6, verticalGap * 0.22));
          let busY = childTopY - clearance;
          const minBusY = parentBottomY + Math.min(14, Math.max(5, verticalGap * 0.12));
          if (busY < minBusY) busY = minBusY;
          if (busY >= childTopY - 2) busY = Math.max(minBusY, childTopY - 3);
          const pathData = `M ${parentX} ${parentBottomY} L ${parentX} ${busY} L ${childX} ${busY} L ${childX} ${childTopY}`;

          return (
            <path
              key={`${equipment.id}-${childId}`}
              d={pathData}
              fill="none"
              stroke="#64748b"
              strokeOpacity={0.55}
              strokeWidth="1.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        });
      })}
    </svg>
  );
}
